import React, { useEffect, useState } from "react";

import { Trans } from "@lingui/react/macro";

import { AvatarProps as MuiAvatarProps } from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";

import { getAvatarSx } from "@readai/common/components/Avatar";
import { getInitials } from "@readai/common/utils";
import { EditIcon } from "@readai/icons/semantic";
import { Column } from "@readai/pulse/components/Stack";

import { AvatarContainer, AvatarSize, StyledAvatar } from "./styles";

interface CommonAvatarProps extends MuiAvatarProps {
  name?: string;
  isEditable?: boolean;
  size?: AvatarSize;
}

interface AvatarPropsWithName {
  name: string;
}

interface AvatarPropsWithChildren {
  children: React.ReactNode;
}

export type AvatarProps = (AvatarPropsWithName | AvatarPropsWithChildren) &
  CommonAvatarProps;

const Avatar = ({
  name,
  size = "sm",
  sx,
  isEditable,
  children,
  onClick,
  ...props
}: AvatarProps) => {
  const [imgError, setImgError] = useState(false);

  const useCustomSx = !props.src || imgError;
  const colorProps = useCustomSx ? getAvatarSx(name) : {};
  const childrenOrInitials = children ?? getInitials(name ?? "") ?? null;

  useEffect(() => {
    if (imgError) {
      setImgError(false);
    }
  }, [props.src]);

  if (isEditable) {
    return (
      <AvatarContainer onClick={onClick}>
        <StyledAvatar
          variant="rounded"
          alt={name}
          {...props}
          size={size}
          sx={{ ...colorProps, ...sx }}
          onError={() => setImgError(true)}
        >
          {childrenOrInitials}
        </StyledAvatar>
        {isEditable && (
          <Column spacing={0.5} variant="center" className="avatar__edit-mask">
            <EditIcon className="avatar__edit-icon" />
            <Typography color="white" variant="pulseLabelMed">
              <Trans>Edit</Trans>
            </Typography>
          </Column>
        )}
      </AvatarContainer>
    );
  }

  return (
    <StyledAvatar
      variant="rounded"
      alt={name}
      {...props}
      onClick={onClick}
      size={size}
      sx={{ ...colorProps, ...sx }}
      onError={() => setImgError(true)}
    >
      {childrenOrInitials}
    </StyledAvatar>
  );
};

export default Avatar;
